import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { distinctGroupRoleNamesByChannelIdsQuery } from 'graphql-queries';
import { PageSizeType } from 'design-system-web';

export type TeamRow = { name: string; groupRoleIds: string[] };

type DistinctGroupRoleNamesByChannelIdsResponse = {
  distinctGroupRoleNames?: {
    items?: { name: string; groupRoleIds: string[] }[];
    pageInfo?: { total: number };
  };
};

const DEFAULT_PAGE_SIZE: PageSizeType = 25;

type Params = {
  channelIds: string[];
  isOpen: boolean;
  onGroupRoleIdsChange: (groupRoleIds: string[]) => void;
};

function extractRows(
  data: DistinctGroupRoleNamesByChannelIdsResponse | undefined
): TeamRow[] {
  const items = data?.distinctGroupRoleNames?.items ?? [];

  return items.map(item => ({
    name: item.name,
    groupRoleIds: item.groupRoleIds,
  }));
}

/**
 * Server-paginated, name-searchable list of distinct group role names for the
 * wizard's team-selection step. Since a row's `groupRoleIds` aren't visible
 * once its page/search results scroll out of view, selections are tracked by
 * name and resolved back to ids via an accumulated name -> ids map, so a
 * team selected under one search term stays resolvable after a later search
 * replaces it on screen.
 */
export function useTeamsSelectionGroupRoles({
  channelIds,
  isOpen,
  onGroupRoleIdsChange,
}: Params) {
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSizeType>(DEFAULT_PAGE_SIZE);
  const [selectedTeamNames, setSelectedTeamNames] = useState<string[]>([]);
  const nameToGroupRoleIdsRef = useRef(new Map<string, string[]>());

  const { data, loading, error } =
    useQuery<DistinctGroupRoleNamesByChannelIdsResponse>(
      distinctGroupRoleNamesByChannelIdsQuery,
      {
        skip: !isOpen || channelIds.length === 0,
        fetchPolicy: 'network-only',
        variables: {
          channelIds,
          name: keyword || undefined,
          pagination: { start: page * pageSize, perPage: pageSize },
        },
      }
    );

  const rows = useMemo(() => extractRows(data), [data]);

  useEffect(() => {
    rows.forEach(row => {
      nameToGroupRoleIdsRef.current.set(row.name, row.groupRoleIds);
    });
  }, [rows]);

  const resolveGroupRoleIds = useCallback((names: string[]) => {
    const ids = names.flatMap(
      name => nameToGroupRoleIdsRef.current.get(name) ?? []
    );

    return Array.from(new Set(ids));
  }, []);

  const selectTeamNames = useCallback(
    (names: string[]) => {
      setSelectedTeamNames(names);
      onGroupRoleIdsChange(resolveGroupRoleIds(names));
    },
    [resolveGroupRoleIds, onGroupRoleIdsChange]
  );

  const handleKeywordChange = useCallback((value: string) => {
    setKeyword(value);
    setPage(0);
  }, []);

  const handlePageChange = useCallback((value: number) => setPage(value), []);

  const handlePageSizeChange = useCallback((value: PageSizeType) => {
    setPageSize(value);
    setPage(0);
  }, []);

  const reset = useCallback(() => {
    setKeyword('');
    setPage(0);
    setPageSize(DEFAULT_PAGE_SIZE);
    setSelectedTeamNames([]);
    nameToGroupRoleIdsRef.current = new Map();
  }, []);

  return {
    rows,
    total: data?.distinctGroupRoleNames?.pageInfo?.total ?? 0,
    loading,
    error: error ? error.message : null,
    keyword,
    page,
    pageSize,
    selectedTeamNames,
    handleKeywordChange,
    handlePageChange,
    handlePageSizeChange,
    selectTeamNames,
    reset,
  };
}
